// src/Hooks/useUnsubscribeManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { db } from '../Firebase/firebase';

export function useUnsubscribeManage() {
  const [activeAction, setActiveAction] = useState('VIEW');
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);

  const [dbRows, setDbRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [mailingList, setMailingList] = useState([]);

  // --- SECURITY LOCK STATE ---
  const [deleteBouncesLock, setDeleteBouncesLock] = useState(true);

  // --- SORTING STATE ---
  const [sortConfig, setSortConfig] = useState({ key: 'userName', direction: 'asc' });

  // --- DATE FILTER STATE ---
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // --- ALERTS ---
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'unsubscribeRequests'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
          };
        });
        setDbRows(fetchedData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching unsubscribe requests:', error);
        setLoading(false);
      },
    );

    const unsubMailingList = onSnapshot(
      collection(db, 'mailingList'),
      (snapshot) => {
        const mlData = snapshot.docs.map((doc) => doc.data());
        setMailingList(mlData);
      },
      (error) => {
        console.error('Error fetching mailing list for cross-reference:', error);
      },
    );

    // AMENDED: Listen for the global delete lock setting to protect data
    const unsubLock = onSnapshot(
      doc(db, 'settings', 'UnsubscribedBouncesLock'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setDeleteBouncesLock(data.locksetting !== false); // Default true unless explicitly false
        } else {
          setDeleteBouncesLock(true);
        }
      },
      (error) => {
        console.error('Error fetching UnsubscribedBouncesLock:', error);
      },
    );

    return () => {
      unsub();
      unsubMailingList();
      unsubLock();
    };
  }, []);

  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) => {
      if (edits[row.id]) {
        return { ...row, ...edits[row.id] };
      }
      return row;
    });
    const newRowIds = new Set(newRows.map((r) => r.id));
    const finalDbRows = mergedDbRows.filter((r) => !newRowIds.has(r.id));

    let combinedRows = [...finalDbRows, ...newRows];

    combinedRows = combinedRows.map((row) => {
      let finalUserName = row.userName;

      // Sanitize payload bugs where email was accidentally saved as userName
      if (
        finalUserName &&
        row.email &&
        finalUserName.toLowerCase().trim() === row.email.toLowerCase().trim()
      ) {
        finalUserName = '';
      }

      // Merge missing details from the mailingList collection dynamically
      if (row.email) {
        const match = mailingList.find(
          (ml) =>
            ml.email && ml.email.toLowerCase().trim() === row.email.toLowerCase().trim(),
        );
        if (match) {
          return {
            ...row,
            companyName: row.companyName || match.companyName || '',
            userName: finalUserName || match.userName || '',
          };
        }
      }
      return { ...row, userName: finalUserName || '' };
    });

    // --- DATE FILTER LOGIC ---
    if (dateFrom || dateTo) {
      const fromTime = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : 0;
      const toTime = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : Infinity;

      combinedRows = combinedRows.filter((row) => {
        if (!row.requestDate) return true; // Keep rows without dates
        const rowTime = new Date(row.requestDate).getTime();
        if (isNaN(rowTime)) return true; // Keep rows with invalid dates
        return rowTime >= fromTime && rowTime <= toTime;
      });
    }

    // --- SORTING LOGIC ---
    if (sortConfig.key) {
      combinedRows.sort((a, b) => {
        let valA = a[sortConfig.key] || '';
        let valB = b[sortConfig.key] || '';

        if (sortConfig.key === 'requestDate') {
          if (!a[sortConfig.key] && b[sortConfig.key])
            return sortConfig.direction === 'asc' ? 1 : -1;
          if (a[sortConfig.key] && !b[sortConfig.key])
            return sortConfig.direction === 'asc' ? -1 : 1;
          if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;

          valA = new Date(a[sortConfig.key]).getTime();
          valB = new Date(b[sortConfig.key]).getTime();

          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return combinedRows;
  }, [dbRows, edits, newRows, mailingList, sortConfig, dateFrom, dateTo]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const checkIsDirty = useCallback(() => {
    return newRows.length > 0 || Object.keys(edits).length > 0;
  }, [newRows, edits]);

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id);
      return [...prev, id];
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? displayRows.map((row) => row.id) : []);
  };

  const handleFieldChange = (id, field, val) => {
    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');
    const isNewRow = newRows.some((r) => r.id === id);
    if (isNewRow) {
      setNewRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: val } : row)),
      );
    } else {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [field]: val },
      }));
    }
  };

  const handleSave = async () => {
    if (!checkIsDirty()) return;
    try {
      const batch = writeBatch(db);
      newRows.forEach((row) => {
        const docRef = doc(db, 'unsubscribeRequests', row.id);
        batch.set(docRef, {
          userName: row.userName || '',
          companyName: row.companyName || '',
          email: row.email || '',
        });
      });
      Object.keys(edits).forEach((id) => {
        const docRef = doc(db, 'unsubscribeRequests', id);
        batch.update(docRef, edits[id]);
      });
      await batch.commit();
      setNewRows([]);
      setEdits({});
      setActiveAction('VIEW');
    } catch (error) {
      console.error('Error saving unsubscribe list:', error);
    }
  };

  const executeDelete = async () => {
    const localIds = [];
    const dbIds = [];
    selectedIds.forEach((id) => {
      if (newRows.some((r) => r.id === id)) localIds.push(id);
      else dbIds.push(id);
    });

    if (localIds.length > 0) {
      setNewRows((prev) => prev.filter((r) => !localIds.includes(r.id)));
    }

    if (dbIds.length > 0) {
      const batch = writeBatch(db);
      dbIds.forEach((id) => {
        batch.delete(doc(db, 'unsubscribeRequests', id));
      });
      await batch.commit();
      setEdits((prev) => {
        const next = { ...prev };
        dbIds.forEach((id) => delete next[id]);
        return next;
      });
    }
    setSelectedIds([]);
    setShowDeleteAlert(false);
  };

  const resetInterface = () => {
    setNewRows([]);
    setEdits({});
    setSelectedIds([]);
    setShowUnsavedAlert(false);
    setActiveAction('VIEW');
  };

  return {
    state: {
      displayRows,
      selectedIds,
      loading,
      focusedRowId,
      sortConfig,
      dateFrom,
      dateTo,
    },
    flags: {
      isDirty: checkIsDirty(),
      // AMENDED: Disables delete icon if no rows are selected OR if the global delete lock is active
      canDelete: selectedIds.length > 0 && !deleteBouncesLock,
      canSave: checkIsDirty(),
      isDeleteLocked: deleteBouncesLock, // Passed to UI for tooltip rendering
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
    },
    actions: {
      handleDeleteRequest: () => selectedIds.length > 0 && setShowDeleteAlert(true),
      handleConfirmDelete: executeDelete,
      handleSave,
      handleCheckboxChange,
      handleSelectAll,
      handleFieldChange,
      setFocusedRowId,
      resetInterface,
      requestSort,
      setDateFrom,
      setDateTo,
    },
  };
}
